import { memo } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import { AVAILABLE_CSV_COLUMNS } from '../../utils/productManagementUtils'

function ExcelDownloadDialog({
  open,
  appliedSearchQuery,
  totalCount,
  downloading,
  error,
  downloadAll,
  rowLimit,
  selectedColumns,
  includeBarcodes,
  includePricing,
  onClose,
  onSubmit,
  onClearError,
  onDownloadAllChange,
  onRowLimitChange,
  onToggleAllColumns,
  onToggleColumn,
  onIncludeBarcodesChange,
  onIncludePricingChange,
}) {
  const sheets = ['Products']
  if (includeBarcodes) sheets.push('Barcodes')
  if (includePricing) sheets.push('Inventory')

  const scopeLabel = downloadAll
    ? appliedSearchQuery?.trim()
      ? `All products matching “${appliedSearchQuery.trim()}”`
      : 'All products in the catalog'
    : `Up to ${rowLimit} product(s)${
        appliedSearchQuery?.trim() ? ` matching “${appliedSearchQuery.trim()}”` : ''
      }`

  const canSubmit = !downloading && selectedColumns.length > 0

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>
        Download products (Excel)
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" onClose={onClearError} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          Large exports can take several minutes. You can leave this page; the job continues in the
          background. Files are kept for 7 days, then removed automatically.
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={downloadAll}
                onChange={(e) => onDownloadAllChange(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography sx={{ fontWeight: 600 }}>Download all matching products</Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Export every product that matches your current search (no row limit)
                </Typography>
              </Box>
            }
          />

          {!downloadAll && (
            <TextField
              label="Number of products to download"
              type="number"
              value={rowLimit}
              onChange={(e) => onRowLimitChange(e.target.value)}
              inputProps={{ min: 1, max: 10000 }}
              helperText={`Current search results: ${totalCount} product(s)`}
              fullWidth
              size="small"
            />
          )}

          <Divider />

          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Extra sheets</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeBarcodes}
                  onChange={(e) => onIncludeBarcodesChange(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Attach barcodes
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Adds a Barcodes sheet (barcode, product name, SKU), sorted by product name.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', mb: 1 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includePricing}
                  onChange={(e) => onIncludePricingChange(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Attach pricing
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Adds an Inventory sheet (SKU, product name, location, selling price, MRP, date
                    modified).
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />
          </Box>

          <Divider />

          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>Product sheet columns</Typography>
              <Button size="small" onClick={onToggleAllColumns} sx={{ minWidth: 'auto', px: 2 }}>
                {selectedColumns.length === AVAILABLE_CSV_COLUMNS.length
                  ? 'Deselect All'
                  : 'Select All'}
              </Button>
            </Box>

            <FormGroup
              sx={{
                maxHeight: 260,
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2,
              }}
            >
              {AVAILABLE_CSV_COLUMNS.map((column) => (
                <FormControlLabel
                  key={column.id}
                  control={
                    <Checkbox
                      checked={selectedColumns.includes(column.id)}
                      onChange={() => onToggleColumn(column.id)}
                      size="small"
                    />
                  }
                  label={column.label}
                />
              ))}
            </FormGroup>

            {selectedColumns.length === 0 && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                Select at least one column to enable Download.
              </Typography>
            )}
          </Box>

          <Alert severity="warning" variant="outlined">
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              What will be exported
            </Typography>
            <Typography variant="body2">{scopeLabel}</Typography>
            <Typography variant="body2">Sheets: {sheets.join(', ')}</Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={downloading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
          onClick={onSubmit}
          disabled={!canSubmit}
          sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#c00000' } }}
        >
          {downloading ? 'Starting…' : 'Start Excel download'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default memo(ExcelDownloadDialog)
